import {
  getCookie,
  readFromLocalStorage,
  writeToLocalStorage,
} from '@jouzen/ecom-utils';

import { SEGMENT_ANONYMOUS_ID_COOKIE } from '@/constants/analytics';
import { debugAnalytics } from '@/utils/debugAnalytics';

export const STORAGE_KEY = 'eop-segment-anonymous-id';
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Create a new anonymousId and write it to local storage
 */
const createAndWriteAnonymousId = (): string => {
  const anonymousId = crypto.randomUUID();

  writeToLocalStorage(STORAGE_KEY, anonymousId);
  debugAnalytics(`Creating new anonymousId`, {
    data: { anonymousId },
  });

  return anonymousId;
};

/**
 * Attempts to get existing anonymousId from local storage.
 * If it has not been initialized, attempts to create a new one.
 * Ensures it is unique from the D2C anonymousId that is stored in cookies.
 * @return null (If after 3 tries, a unique anonymousId cannot be generated)
 * @return string (If a unique anonymousId was generated, it is returned)
 */
export function getAnonymousId(): string | null {
  const d2cAnonymousId = getCookie(SEGMENT_ANONYMOUS_ID_COOKIE);
  let eopAnonymousId = readFromLocalStorage(STORAGE_KEY);
  let retryAttempts = 0;

  if (!eopAnonymousId) {
    eopAnonymousId = createAndWriteAnonymousId();

    // Assign a new anonymousId if it matches the D2C anonymousId. Max 3 attempts.
    while (
      d2cAnonymousId === eopAnonymousId &&
      retryAttempts < MAX_RETRY_ATTEMPTS
    ) {
      retryAttempts++;
      debugAnalytics(`Anonymous ID is same as ecom`, {
        data: {
          anonymousId: eopAnonymousId,
          ecomAnonymousId: d2cAnonymousId,
          retryAttempts,
        },
        level: 'warn',
        logToDatadog: 'getAnonymousId',
      });
      eopAnonymousId = createAndWriteAnonymousId();
    }
  }

  // If they still match after 3 retries, return null
  if (d2cAnonymousId === eopAnonymousId) {
    debugAnalytics(`Anonymous ID is same as ecom after max retries`, {
      data: {
        anonymousId: eopAnonymousId,
        ecomAnonymousId: d2cAnonymousId,
        retryAttempts,
      },
      level: 'error',
      logToDatadog: 'getAnonymousId',
    });

    return null;
  }

  return eopAnonymousId;
}
